<template>
	<section class="scene-map">
		<ChalkyFormSearch
			queryParameter="search"
			type="location"
			v-on:click:badge="Handle_OnClickBadge"
			v-on:search:start="Handle_OnSearchStart"
			v-on:search:location="Handle_OnSearchLocation"
		/>

		<!-- <ChalkyVenueList v-on:venue:select="Handle_OnSelectVenue" /> -->

		<ChalkyVenueCardPreview
			class="on-light shadow"
			v-bind:venueModel="venueModel"
			v-if="venueModel.id"
			v-on:click:gallery="Handle_OnClickGotoVenue"
		>
			<template v-slot:content:after>
				<button id="btnSeeMore" class="control type-right" v-on:click="Handle_OnClickGotoVenue">
					Go to venue
				</button>
			</template>
		</ChalkyVenueCardPreview>

		<ViewUiMediaMap
			v-bind:centerOffsetY="centerOffsetY"
			v-bind:mapLatitude="mapLatitude"
			v-bind:mapLongitude="mapLongitude"
			v-bind:mapZoom="mapZoom"
			v-bind:venueCollection="venueCollection"
			v-on:click="Handle_OnClickMap"
			v-on:map:drag="Handle_OnDragMap"
			v-on:map:zoom="Handle_OnZoomMap"
			v-on:marker:click="Handle_OnSelectVenue"
		/>
	</section>
</template>

<script lang="ts">
	import ChalkySticks from '@chalkysticks/sdk';
	import SceneBase from '@/Core/Scene.vue';
	import { Component, Prop, Watch } from 'vue-property-decorator';
	import { beforeDestroy, bind, mounted } from '@/Utility/Decorators';

	/**
	 * Interface for Google Map position data.
	 */
	interface IGoogleMapPosition {
		latitude: number;
		longitude: number;
		zoom: number;
	}

	/**
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package Scene
	 * @project ChalkySticks App
	 */
	@Component
	export default class SceneMap extends SceneBase {
		/**
		 * @type ChalkySticks/Collection/Venue
		 */
		@Prop({
			default: () =>
				ChalkySticks.Factory.Venue.collection({
					limit: 64,
				}),
		})
		public venueCollection!: ChalkySticks.Collection.Venue;

		/**
		 * @type ChalkySticks/Model/Venue
		 */
		@Prop({
			default: () => ChalkySticks.Factory.Venue.model(),
		})
		public venueModel!: ChalkySticks.Model.Venue;

		/**
		 * @type boolean
		 */
		protected followUser: boolean = true;

		/**
		 * @type number
		 */
		protected mapLatitude: number = 0;

		/**
		 * @type number
		 */
		protected mapLongitude: number = 0;

		/**
		 * @type number
		 */
		protected mapZoom: number = 13;

		/**
		 * @return void
		 */
		@mounted
		public attachEvents(): void {
			ChalkySticks.Core.Event.Bus.on('location:change', this.Handle_OnLocationChange);
			window.addEventListener('popstate', this.Handle_OnRoutePop);
		}

		/**
		 * @return void
		 */
		@beforeDestroy
		public detachEvents(): void {
			ChalkySticks.Core.Event.Bus.off('location:change', this.Handle_OnLocationChange);
			window.removeEventListener('popstate', this.Handle_OnRoutePop);
		}

		/**
		 * @return Promise<void>
		 */
		@mounted
		public async activate(): Promise<void> {
			// Don't set the location if we're searching
			if (this.$route.query.search) {
				this.followUser = false;
				return;
			}

			// Assign coordinates
			const hashCoordinates = this.parseCoordinatesFromHash();

			if (hashCoordinates) {
				this.followUser = false;
				this.mapLatitude = hashCoordinates.latitude;
				this.mapLongitude = hashCoordinates.longitude;

				// The venues for some reason wont load properly if we don't
				// use a delay. Not sure if it's a bounds issue?
				setTimeout(() => {
					this.mapZoom = hashCoordinates.zoom || this.mapZoom;
				}, 250);
				return;
			}

			this.mapLatitude = this.$store.getters['location/latitude'] || 0;
			this.mapLongitude = this.$store.getters['location/longitude'] || 0;
		}

		/**
		 * @return void
		 */
		protected resetVenue(): void {
			this.venueModel.reset();
			this.venueModel.id = '';
		}

		/**
		 * Parse coordinates from the URL hash and set them to the map.
		 *
		 * @return boolean
		 */
		protected parseCoordinatesFromHash(): boolean | any {
			const hash = location.hash.replace('#', '');
			const params = new URLSearchParams(hash);
			const latitude = params.get('latitude') || '';
			const longitude = params.get('longitude') || '';
			const zoom = params.get('zoom') || '13';

			if (latitude && longitude) {
				const coordinates = ChalkySticks.Utility.Geolocation.simplifyCoordinates(
					Number(latitude),
					Number(longitude),
					1e2,
				);

				return {
					...coordinates,
					zoom: Number(zoom),
				};
			}

			return false;
		}

		/**
		 * Adjust the zoom level based on the nearest venue's distance from
		 * the given coordinates. Zooms in for nearby venues, zooms out for
		 * rural areas where venues are sparse or nonexistent.
		 *
		 * @param number latitude
		 * @param number longitude
		 * @return void
		 */
		protected zoomToLocalVenue(latitude: number, longitude: number): void {
			const venueModel = this.venueCollection.first();

			// No venues found at all, zoom out to show a wider area
			if (!venueModel) {
				this.mapZoom = 8;
				return;
			}

			// Get distance in km to nearest venue
			const distance = venueModel.getDistance(latitude, longitude);

			// Zoom level based on distance to nearest venue
			if (distance <= 0.015) {
				this.mapZoom = 18;
				this.selectVenue(venueModel);
			} else if (distance <= 0.05) {
				this.mapZoom = 18;
			} else if (distance <= 0.4) {
				this.mapZoom = 15;
			} else if (distance <= 5) {
				this.mapZoom = 13;
			} else if (distance <= 20) {
				this.mapZoom = 11;
			} else if (distance <= 50) {
				this.mapZoom = 9;
			} else {
				this.mapZoom = 7;
			}
		}

		/**
		 * @param ChalkySticks.Model.Venue venueModel
		 * @return void
		 */
		protected selectVenue(venueModel: ChalkySticks.Model.Venue): void {
			this.venueModel = venueModel;
			this.followUser = false;
		}

		/**
		 * Sync the current map position to the URL hash.
		 *
		 * @param IGoogleMapPosition position
		 * @return void
		 */
		protected syncRoute(position: IGoogleMapPosition): void {
			const coordinates = ChalkySticks.Utility.Geolocation.simplifyCoordinates(
				position.latitude,
				position.longitude,
				1e2,
			);

			// Stop tracking the user
			this.followUser = false;

			// Build new route with hash
			const newRoute: any = {
				...this.$route,
				hash: `#latitude=${coordinates.latitude}&longitude=${coordinates.longitude}&zoom=${position.zoom}`,
				query: { ...this.$route.query },
			};

			// Remove search query if present
			if (newRoute.query.search) {
				delete newRoute.query.search;
			}

			// Use replace to avoid triggering popstate
			this.$router.replace(newRoute);
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * @return Promise<void>
		 */
		protected async Handle_OnClickGotoVenue(): Promise<void> {
			this.$store.dispatch('gotoVenue', this.venueModel);
		}

		/**
		 * @return Promise<void>
		 */
		protected async Handle_OnClickBadge(): Promise<void> {
			this.followUser = true;
			this.mapLatitude = this.$store.getters['location/latitude'] || this.mapLatitude;
			this.mapLongitude = this.$store.getters['location/longitude'] || this.mapLongitude;
		}

		/**
		 * @return Promise<void>
		 */
		protected async Handle_OnClickMap(): Promise<void> {
			this.resetVenue();
		}

		/**
		 * @param IGoogleMapPosition position
		 * @return Promise<void>
		 */
		protected async Handle_OnDragMap(position: IGoogleMapPosition): Promise<void> {
			this.syncRoute(position);
		}

		/**
		 * @param ChalkySticks.Core.IDispatcherEvent<ChalkySticks.Core.IGeolocationPayload position>
		 * @return Promise<void>
		 */
		@bind
		protected async Handle_OnLocationChange(
			e: ChalkySticks.Core.IDispatcherEvent<ChalkySticks.Core.IGeolocationPayload>,
		): Promise<void> {
			if (this.followUser) {
				this.mapLatitude = e.data.position.coords.latitude;
				this.mapLongitude = e.data.position.coords.longitude;
			}
		}

		/**
		 * @param Event e
		 * @return Promise<void>
		 */
		@bind
		protected async Handle_OnRoutePop(e: Event): Promise<void> {
			this.activate();
		}

		/**
		 * @param ChalkySticks.Model.Geocode geocodeModel
		 * @return Promise<void>
		 */
		protected async Handle_OnSearchLocation(geocodeModel: ChalkySticks.Model.Geocode): Promise<void> {
			const { latitude, longitude } = geocodeModel.getLocation();

			ChalkySticks.Utility.Analytics.track('map:search', {
				address: geocodeModel.getFullAddress(),
				city: geocodeModel.getCity(),
				country: geocodeModel.getCountry(),
				latitude: latitude,
				longitude: longitude,
			});

			ChalkySticks.Core.Event.Bus.dispatch('keyboard:hide.request');

			this.resetVenue();

			this.mapLatitude = latitude;
			this.mapLongitude = longitude;

			this.venueCollection.setQueryParam('latitude', this.mapLatitude.toString());
			this.venueCollection.setQueryParam('longitude', this.mapLongitude.toString());
			await this.venueCollection.fetch();

			this.zoomToLocalVenue(latitude, longitude);
		}

		/**
		 * @param string query
		 * @return Promise<void>
		 */
		protected async Handle_OnSearchStart(query: string): Promise<void> {
			this.followUser = false;

			// Remove coordinates
			location.hash = '';
		}

		/**
		 * @param ChalkySticks.Model.Venue venueModel
		 * @return Promise<void>
		 */
		protected async Handle_OnSelectVenue(venueModel: ChalkySticks.Model.Venue): Promise<void> {
			this.selectVenue(venueModel);
		}

		/**
		 * @param IGoogleMapPosition position
		 * @return Promise<void>
		 */
		protected async Handle_OnZoomMap(position: IGoogleMapPosition): Promise<void> {
			this.syncRoute(position);
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	.scene-map {
		height: 100%;

		.chalky.form-search {
			bottom: 1.5em;
			left: 0.5em;
			position: absolute;
			right: 0.5em;
			transition: transform 0.5s var(--ease-in-out-quad);
			z-index: var(--z-page-mid);
		}

		.chalky.venue-cardpreview {
			bottom: calc(1.5em + 80px);
			cursor: pointer;
			left: 0.5em;
			position: absolute;
			right: 0.5em;
			transition: transform 0.5s var(--ease-in-out-quad);
			z-index: var(--z-page-mid);
		}

		.chalky.venue-map {
			.gm-bundled-control {
				// mk: lets just hide the zoom buttons and stuff
				display: none;

				// margin-bottom: 100px !important;
			}

			.loading-indicator {
				bottom: 100px;
				top: auto;
			}
		}
	}

	// State
	// ---------------------------------------------------------------------------

	#app.state-keyboard .scene-map .chalky.form-search {
		// transform: translateY(calc(var(--keyboard-height) * -1));
	}

	#app.state-keyboard .scene-map .chalky.venue-cardpreview {
		display: none;
	}

	// Desktop
	// ---------------------------------------------------------------------------

	@include media-query('>= tablet') {
		.scene-map {
			.chalky.venue-cardpreview {
				max-width: 480px;
				width: 50%;
			}
		}
	}
</style>
